import { MindMapData } from '../models/types';

export interface OutlineDocument {
  text: string;
  lineToNodeId: Record<number, string>;
  nodeIdToLine: Record<string, number>;
}

export interface OutlineTreeNode {
  id: string;
  topic: string;
  children: OutlineTreeNode[];
  expanded: boolean;
  lineIndex: number;
}

export interface OutlineFragmentNode {
  topic: string;
  children: OutlineFragmentNode[];
}

export interface OutlineParseError {
  line: number;
  code: OutlineErrorCode;
}

export type OutlineIdStrategy = (lineIndex: number) => string;
export type OutlineErrorCode =
  | 'invalidIndentation'
  | 'onlyFirstLineRoot'
  | 'invalidIndentationLevel'
  | 'rootMustBeLevelZero'
  | 'parentNotFound'
  | 'invalidOutline'
  | 'emptyOutline';

export const ROOT_NODE_ID = 'root';
export const NODE_ID_PREFIX = 'node-';
export const defaultOutlineIdStrategy: OutlineIdStrategy = lineIndex =>
  lineIndex === 0 ? ROOT_NODE_ID : `${NODE_ID_PREFIX}${lineIndex}`;

interface OutlineNode {
  id: string;
  topic: string;
  children?: OutlineNode[];
}

interface MindMapTreeNode {
  id?: string;
  topic?: string;
  children?: MindMapTreeNode[];
  direction?: unknown;
  expanded?: unknown;
  [key: string]: unknown;
}

const normalizeLines = (text: string): string[] => {
  const normalized = text.replace(/\r\n?/g, '\n');
  const lines = normalized.split('\n');
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
    lines.pop();
  }
  return lines;
};

const parseIndent = (line: string): { depth: number; error?: OutlineErrorCode } => {
  let tabs = 0;
  let spaces = 0;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '\t') {
      tabs += 1;
    } else if (ch === ' ') {
      spaces += 1;
    } else {
      break;
    }
  }
  if (spaces % 2 !== 0) {
    return { depth: 0, error: 'invalidIndentation' };
  }
  return { depth: tabs + Math.floor(spaces / 2) };
};

export const toOutlineDocument = (mindData: MindMapData | null): OutlineDocument => {
  if (!mindData || mindData.format !== 'node_tree' || !mindData.data) {
    return { text: '', lineToNodeId: {}, nodeIdToLine: {} };
  }

  const lines: string[] = [];
  const lineToNodeId: Record<number, string> = {};
  const nodeIdToLine: Record<string, number> = {};

  const walk = (node: OutlineNode, depth: number): void => {
    const line = `${'\t'.repeat(depth)}${node.topic ?? ''}`;
    const lineIndex = lines.length;
    lines.push(line);
    lineToNodeId[lineIndex] = node.id;
    nodeIdToLine[node.id] = lineIndex;
    (node.children ?? []).forEach(child => walk(child, depth + 1));
  };

  walk(mindData.data as OutlineNode, 0);

  return { text: lines.join('\n'), lineToNodeId, nodeIdToLine };
};

export const toTabbedOutlineFragment = (node: { topic?: string; children?: Array<unknown> }): string => {
  const lines: string[] = [];

  const walk = (current: { topic?: string; children?: Array<unknown> }, depth: number): void => {
    lines.push(`${'\t'.repeat(depth)}${current.topic ?? ''}`);
    (current.children ?? []).forEach(child => {
      if (child && typeof child === 'object') {
        walk(child as { topic?: string; children?: Array<unknown> }, depth + 1);
      }
    });
  };

  walk(node, 0);
  return lines.join('\n');
};

export const fromTabbedOutlineFragment = (
  text: string
): { nodes: OutlineFragmentNode[]; error?: OutlineParseError } => {
  const lines = normalizeLines(text);
  if (lines.length === 0) {
    return { nodes: [], error: { line: 1, code: 'emptyOutline' } };
  }

  const parsedLines: Array<{ depth: number; topic: string }> = [];
  let minDepth: number | null = null;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const { depth, error } = parseIndent(rawLine);
    if (error) {
      return { nodes: [], error: { line: lineIndex + 1, code: error } };
    }
    parsedLines.push({ depth, topic: rawLine.trim() });
    minDepth = minDepth === null ? depth : Math.min(minDepth, depth);
  }

  const roots: OutlineFragmentNode[] = [];
  const stack: OutlineFragmentNode[] = [];
  const baseDepth = minDepth ?? 0;

  for (let lineIndex = 0; lineIndex < parsedLines.length; lineIndex += 1) {
    const line = parsedLines[lineIndex];
    const depth = line.depth - baseDepth;
    if (depth > stack.length) {
      return { nodes: [], error: { line: lineIndex + 1, code: 'invalidIndentationLevel' } };
    }

    const node: OutlineFragmentNode = { topic: line.topic, children: [] };
    if (depth === 0) {
      roots.push(node);
    } else {
      const parent = stack[depth - 1];
      if (!parent) {
        return { nodes: [], error: { line: lineIndex + 1, code: 'parentNotFound' } };
      }
      parent.children.push(node);
    }
    stack.splice(depth, stack.length - depth, node);
  }

  return { nodes: roots };
};

export const fromOutlineText = (
  text: string,
  meta?: MindMapData['meta'],
  idStrategy: OutlineIdStrategy = defaultOutlineIdStrategy
): {
  mindData: MindMapData | null;
  error?: OutlineParseError;
  lineToNodeId: Record<number, string>;
  nodeIdToLine: Record<string, number>;
} => {
  const lines = normalizeLines(text);
  const lineToNodeId: Record<number, string> = {};
  const nodeIdToLine: Record<string, number> = {};

  if (lines.length === 0) {
    return { mindData: null, error: { line: 1, code: 'emptyOutline' }, lineToNodeId, nodeIdToLine };
  }

  const stack: OutlineNode[] = [];
  let root: OutlineNode | null = null;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const { depth, error } = parseIndent(rawLine);
    if (error) {
      return {
        mindData: null,
        error: { line: lineIndex + 1, code: error },
        lineToNodeId,
        nodeIdToLine,
      };
    }

    if (lineIndex > 0 && depth === 0) {
      return {
        mindData: null,
        error: { line: lineIndex + 1, code: 'onlyFirstLineRoot' },
        lineToNodeId,
        nodeIdToLine,
      };
    }

    if (depth > stack.length) {
      return {
        mindData: null,
        error: { line: lineIndex + 1, code: 'invalidIndentationLevel' },
        lineToNodeId,
        nodeIdToLine,
      };
    }

    const topic = rawLine.trim();
    const id = idStrategy(lineIndex);
    const node: OutlineNode = { id, topic, children: [] };

    lineToNodeId[lineIndex] = id;
    nodeIdToLine[id] = lineIndex;

    if (lineIndex === 0) {
      if (depth !== 0) {
        return {
          mindData: null,
          error: { line: 1, code: 'rootMustBeLevelZero' },
          lineToNodeId,
          nodeIdToLine,
        };
      }
      root = node;
      stack.push(node);
      continue;
    }

    const parent = stack[depth - 1];
    if (!parent) {
      return {
        mindData: null,
        error: { line: lineIndex + 1, code: 'parentNotFound' },
        lineToNodeId,
        nodeIdToLine,
      };
    }

    parent.children = parent.children ?? [];
    parent.children.push(node);

    stack.splice(depth, stack.length - depth, node);
  }

  if (!root) {
    return { mindData: null, error: { line: 1, code: 'invalidOutline' }, lineToNodeId, nodeIdToLine };
  }

  return {
    mindData: {
      meta,
      format: 'node_tree',
      data: root,
    },
    lineToNodeId,
    nodeIdToLine,
  };
};

export const toOutlineTreeNodes = (mindData: MindMapData | null): OutlineTreeNode[] => {
  if (!mindData || mindData.format !== 'node_tree' || !mindData.data) {
    return [];
  }

  let lineIndex = 0;
  const nodes: OutlineTreeNode[] = [];

  const walk = (node: MindMapTreeNode): OutlineTreeNode => {
    const currentIndex = lineIndex;
    const treeNode: OutlineTreeNode = {
      id: node.id ?? defaultOutlineIdStrategy(currentIndex),
      topic: node.topic ?? '',
      children: (node.children ?? []).map(child => walk(child)),
      expanded: true,
      lineIndex: currentIndex,
    };
    lineIndex += 1;
    return treeNode;
  };

  const root = mindData.data;
  if (root && typeof root === 'object') {
    nodes.push(walk(root as MindMapTreeNode));
  }
  return nodes;
};

